// Sign-in with the accounts email magic link. In dev, accounts returns the link in the response
// (`dev.token`, DEV_RETURN_MAGIC_LINK=true); the page offers to open it, which calls verify.

import { api, loadTokens, saveTokens, type Tokens } from "./api.ts";

export interface Me {
  id: string;
  email: string;
  handle: string | null;
  displayName: string | null;
  avatarRef: string | null;
  /** structured avatar (platform wave 2); absent on older accounts builds */
  avatar?: unknown;
  /** the saved avatar's picture (avatar editor slice 2); absent on older accounts builds */
  avatarPortrait?: string | null;
  ageBand: "13-17" | "18+";
  createdAt: number;
  /** wave 12: first run finished (null: still to do); absent on older accounts builds */
  onboardedAt?: number | null;
}

/** Shown on the sign-up screen; accounts sends `playtime_on_by_default` for new accounts and clients must show it. */
export interface Notice {
  code: string;
  title: string;
  message: string;
  setting?: string;
}

export interface StartResult {
  status: "sent";
  newAccount: boolean;
  expiresAt: number;
  notices?: Notice[];
  dev?: { token: string; link: string };
}

let me: Me | null = null;
let loaded = false;
const listeners = new Set<() => void>();

export function onSessionChange(fn: () => void): void {
  listeners.add(fn);
}
function changed(): void {
  for (const fn of listeners) fn();
}

export function signedIn(): boolean {
  return loadTokens() !== null;
}

export function currentMe(): Me | null {
  return me;
}

export async function loadMe(force = false): Promise<Me | null> {
  if (loaded && !force) return me;
  if (!signedIn()) {
    me = null;
    loaded = true;
    return null;
  }
  try {
    me = await api<Me>("accounts", "/me");
  } catch {
    me = null;
  }
  loaded = true;
  return me;
}

export function startSignIn(email: string, age?: number): Promise<StartResult> {
  // client "web": accounts answers with a https://<site>/signin?token= link instead of the launcher's thrdm:// one
  return api<StartResult>("accounts", "/auth/email/start", { body: age === undefined ? { email, client: "web" } : { email, age, client: "web" }, auth: false });
}

export interface Verified {
  user: Me;
  /** this verify made the account (accounts sends the sign-up notices then) */
  created: boolean;
  notices: Notice[];
}

export async function verifySignIn(token: string): Promise<Verified> {
  return adoptSession(await api<SessionReply>("accounts", "/auth/email/verify", { body: { token }, auth: false }));
}

/** What every accounts sign-in answers (magic link, Discord/GitHub, passkey): tokens, Me, and sign-up notices. */
export type SessionReply = Tokens & { user: Me; created?: boolean; notices?: Notice[] };

/** Signs this browser in with a sign-in's answer (wave 12: the other sign-in methods end here too). */
export function adoptSession(r: SessionReply): Verified {
  saveTokens({ accessToken: r.accessToken, accessExpiresAt: r.accessExpiresAt, refreshToken: r.refreshToken, refreshExpiresAt: r.refreshExpiresAt });
  me = r.user;
  loaded = true;
  changed();
  return { user: r.user, created: r.created === true, notices: r.notices ?? [] };
}

export async function signOut(): Promise<void> {
  const t = loadTokens();
  if (t) await api("accounts", "/auth/signout", { body: { refreshToken: t.refreshToken }, auth: false }).catch(() => {});
  saveTokens(null);
  me = null;
  changed();
}

/** "@handle", the display name, or the email's local part. */
export function nameOf(m: Pick<Me, "handle" | "displayName"> & { email?: string }): string {
  return m.displayName ?? (m.handle ? `@${m.handle}` : (m.email?.split("@")[0] ?? "you"));
}

/** Pulls a magic-link token out of what someone pasted: a bare `ml_…` token or a link with `?token=`. */
export function tokenFromInput(input: string): string | null {
  const s = input.trim();
  if (/^ml_[A-Za-z0-9_-]+$/.test(s)) return s;
  const m = /[?&]token=(ml_[A-Za-z0-9_-]+)/.exec(s);
  return m ? m[1]! : null;
}
